import { Box } from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Fragment, ReactElement, ReactNode, useCallback, useMemo } from 'react';

import {
  useAttachedScrollParent,
  useListScrollMargin,
  useScrollToVirtualIndex,
} from './hooks';
import { shouldVirtualizeList } from './model';
import { virtualizedListContainerSx, virtualizedListRowSx } from './styles';
import { VirtualizedListProps, VirtualizedWindowProps } from './types';

const DEFAULT_OVERSCAN = 7;

function measureRowHeight(element: Element): number {
  return element.getBoundingClientRect().height;
}

function VirtualizedListWindow<T>({
  items,
  getItemKey,
  estimateSize,
  renderItem,
  overscan = DEFAULT_OVERSCAN,
  scrollToIndex = null,
  scrollToKey,
  rowSx,
}: VirtualizedWindowProps<T>): ReactElement {
  const { listRef, attachList, scrollParent } = useAttachedScrollParent(
    items.length
  );
  const scrollMargin = useListScrollMargin(listRef, scrollParent);

  // TanStack rebuilds every measurement when `getItemKey` changes identity,
  // so it may change only together with `items`.
  const getVirtualItemKey = useCallback(
    (index: number) => getItemKey(items[index], index),
    [getItemKey, items]
  );
  const estimateVirtualItemSize = useCallback(
    (index: number) => estimateSize(items[index], index),
    [estimateSize, items]
  );

  // TanStack Virtual returns functions that React Compiler cannot memoize.
  // eslint-disable-next-line react-hooks/incompatible-library
  const virtualizer = useVirtualizer({
    count: items.length,
    enabled: scrollParent !== null,
    getScrollElement: () => scrollParent,
    scrollMargin,
    estimateSize: estimateVirtualItemSize,
    overscan,
    getItemKey: getVirtualItemKey,
    measureElement: measureRowHeight,
  });

  useScrollToVirtualIndex(virtualizer, scrollParent, scrollToIndex, scrollToKey);

  const combinedRowSx = useMemo(
    () => (rowSx ? [virtualizedListRowSx, rowSx] : virtualizedListRowSx),
    [rowSx]
  );

  const virtualItems = scrollParent ? virtualizer.getVirtualItems() : [];
  const reservedHeight = scrollParent
    ? virtualizer.getTotalSize()
    : items.reduce(
        (sum, item, index) => sum + estimateSize(item, index),
        0
      );

  return (
    <Box
      ref={attachList}
      sx={{
        ...virtualizedListContainerSx,
        height: `${reservedHeight}px`,
      }}
    >
      {virtualItems.map((virtualItem) => {
        const item = items[virtualItem.index];
        return (
          <Box
            key={virtualItem.key}
            data-index={virtualItem.index}
            ref={virtualizer.measureElement}
            sx={combinedRowSx}
            style={{
              transform: `translateY(${virtualItem.start - scrollMargin}px)`,
            }}
          >
            {renderItem(item, virtualItem.index)}
          </Box>
        );
      })}
    </Box>
  );
}

export function VirtualizedList<T>({
  items,
  minItemCount = 0,
  renderItem,
  getItemKey,
  ...windowProps
}: VirtualizedListProps<T>): ReactNode {
  if (!shouldVirtualizeList(items.length, minItemCount)) {
    return (
      <>
        {items.map((item, index) => (
          <Fragment key={getItemKey(item, index)}>
            {renderItem(item, index)}
          </Fragment>
        ))}
      </>
    );
  }

  return (
    <VirtualizedListWindow
      items={items}
      getItemKey={getItemKey}
      renderItem={renderItem}
      {...windowProps}
    />
  );
}

export type { VirtualizedListProps } from './types';
